// DOM HUD: score, combo, wave, rechargeable ship energy, quota and banners.

import type { CultMember } from './roster';
import type { AwardDef } from './achievements';

const el = {
  hud: () => document.getElementById('hud')!,
  score: () => document.getElementById('score')!,
  combo: () => document.getElementById('combo')!,
  wave: () => document.getElementById('wave')!,
  hull: () => document.getElementById('hull')!,
  quota: () => document.getElementById('quota')!,
  toast: () => document.getElementById('toast')!,
  carry: () => document.getElementById('carry')!,
  carryArrow: () => document.getElementById('carry-arrow')!,
  carryDistance: () => document.getElementById('carry-distance')!,
  threat: () => document.getElementById('threat')!,
  threatArrow: () => document.getElementById('threat-arrow')!,
  threatLabel: () => document.getElementById('threat-label')!,
};

let toastTimer: ReturnType<typeof setTimeout> | null = null;
let damageTimer: ReturnType<typeof setTimeout> | null = null;

// Naming a leader is the reward for the lift, and the same beat fires static,
// soul and council calls. Without a floor the card was legible for one frame
// before the next line overwrote it, which reads as noise rather than two
// messages. One line may wait; only the newest waits.
let bannerHoldUntil = 0;
let pendingBanner: [string, number] | null = null;
let pendingTimer: ReturnType<typeof setTimeout> | null = null;

function clearPendingBanner(): void {
  if (pendingTimer) clearTimeout(pendingTimer);
  pendingTimer = null;
  pendingBanner = null;
  bannerHoldUntil = 0;
}

export function showHud(show: boolean): void {
  el.hud().hidden = !show;
  // A held line must not outlive the flight and surface over the next one.
  if (!show) { clearDamageState(); hideBanner(); }
}

export function setScore(score: number): void {
  el.score().textContent = score.toLocaleString('en-GB');
}

export function setCombo(
  mult: number, active: boolean, fraction = 0, need = 0, nextMult = 0,
): void {
  const node = el.combo();
  node.hidden = !active;
  const fill = document.getElementById('combo-fill')!;
  if (!active) {
    node.dataset.urgent = '';
    node.style.setProperty('--combo-frac', '0');
    fill.style.transform = 'scaleX(0)';
    return;
  }
  const clamped = Math.max(0, Math.min(1, fraction));
  // The rung you are on has never been the interesting number. How many lifts
  // buy the next one is the thing that turns a chain into a sprint.
  const climb = need > 0 && nextMult > mult ? ` · ${need}→x${nextMult}` : '';
  const label = `x${mult} CHAIN${climb}`;
  const labelNode = document.getElementById('combo-label')!;
  if (labelNode.textContent !== label) {
    labelNode.textContent = label;
  }
  const aria = climb
    ? `${mult} times chain, ${need} more for ${nextMult} times`
    : `${mult} times chain`;
  if (node.getAttribute('aria-label') !== aria) node.setAttribute('aria-label', aria);
  node.dataset.urgent = clamped <= 0.25 ? '1' : '';
  node.style.setProperty('--combo-frac', String(clamped));
  fill.style.transform = `scaleX(${clamped})`;
}

let shownWave = 1;
let waveMastery = false;

function renderWave(): void {
  const node = el.wave();
  const base = `WAVE ${shownWave}`;
  node.textContent = waveMastery ? `${base} · ★ CLEAN` : base;
  node.dataset.mastery = waveMastery ? '1' : '';
  const mobile = document.getElementById('mobile-wave')!;
  mobile.textContent = waveMastery ? `W${shownWave} · ★` : `WAVE ${shownWave}`;
  mobile.dataset.mastery = waveMastery ? '1' : '';
}

export function setWave(wave: number): void {
  shownWave = wave;
  renderWave();
}

export function setWaveMastery(active: boolean): void {
  waveMastery = active;
  renderWave();
}

export function setHull(hull: number, max = 3): void {
  const node = el.hull();
  const remaining = Math.max(0, hull);
  const capacity = Math.max(remaining, max);
  const fraction = capacity > 0 ? Math.min(1, remaining / capacity) : 0;
  const percent = Math.round(fraction * 100);
  const critical = remaining > 0 && fraction <= 0.25;
  const siphon = critical;
  document.getElementById('hull-label')!.textContent = critical ? 'LOW · ×2' : 'ENERGY';
  document.getElementById('hull-fill')!.style.transform = `scaleX(${fraction})`;
  document.getElementById('hull-value')!.textContent = `${percent}%`;
  node.dataset.critical = critical ? '1' : '';
  node.setAttribute('aria-valuenow', String(percent));
  node.setAttribute('aria-valuemax', '100');
  node.setAttribute(
    'aria-label',
    `Ship energy ${percent} percent${critical ? '. Low energy' : ''}${siphon ? '. Emergency siphon active' : ''}`,
  );
  document.getElementById('damage-vignette')!.dataset.critical = critical ? '1' : '';
}

/** Actual energy loss: screen-edge impact plus a meter kick. Shields never call it. */
export function damage(critical: boolean): void {
  const vignette = document.getElementById('damage-vignette')!;
  const hull = el.hull();
  vignette.dataset.critical = critical ? '1' : '';
  // Restart the impact even if two scripted hits happen inside one animation.
  vignette.dataset.hit = '';
  hull.dataset.hit = '';
  void vignette.offsetWidth;
  vignette.dataset.hit = '1';
  hull.dataset.hit = '1';
  if (damageTimer) clearTimeout(damageTimer);
  damageTimer = setTimeout(() => {
    vignette.dataset.hit = '';
    hull.dataset.hit = '';
    damageTimer = null;
  }, 560);
}

function clearDamageState(): void {
  if (damageTimer) clearTimeout(damageTimer);
  damageTimer = null;
  const vignette = document.getElementById('damage-vignette')!;
  vignette.dataset.hit = '';
  vignette.dataset.critical = '';
  const hull = el.hull();
  hull.dataset.hit = '';
  hull.dataset.critical = '';
}

export function setQuota(done: number, quota: number, council = 0, councilSize = 0): void {
  const goal = councilSize > 0 ? `  ·  COUNCIL ${council}/${councilSize}` : '';
  document.getElementById('quota-counts')!.textContent = `HARVEST ${done}/${quota}${goal}`;
  document.getElementById('mobile-quota')!.textContent = councilSize > 0
    ? `HARVEST ${done}/${quota} · COUNCIL ${council}/${councilSize}`
    : `HARVEST ${done}/${quota}`;
}

/** The ending only has weight when the pilot can see the road to it. Keep this
 * hidden through the first lesson, then distinguish cleared/current/future. */
export function setTour(stage: number, label: string, visible: boolean): void {
  const node = document.getElementById('tour-progress')!;
  const current = Math.max(1, Math.min(7, Math.floor(stage)));
  document.getElementById('mobile-land')!.textContent = label;
  document.getElementById('mobile-stage')!.textContent = `LEVEL ${current}/7`;
  node.hidden = !visible;
  if (!visible) return;
  document.getElementById('tour-label')!.textContent = `${label} · ${current}/7`;
  node.setAttribute('aria-label', `Tour progress. ${label}. Land ${current} of 7.`);
  const stops = document.getElementById('tour-track')!.children;
  for (let i = 0; i < stops.length; i++) {
    (stops[i] as HTMLElement).dataset.state = i + 1 < current
      ? 'cleared'
      : i + 1 === current ? 'current' : '';
  }
}

export function toastMember(member: CultMember): void {
  const node = el.toast();
  clearPendingBanner();
  bannerHoldUntil = performance.now() + 900;
  node.innerHTML = '';
  const name = document.createElement('div');
  name.className = 't-name';
  name.textContent = `${member.name.toUpperCase()} ABDUCTED  +600`;
  const role = document.createElement('div');
  role.className = 't-role';
  role.textContent = member.role;
  const lore = document.createElement('div');
  lore.className = 't-lore';
  lore.textContent = member.tagline;
  node.append(name, role, lore);
  node.hidden = false;
  if (toastTimer) clearTimeout(toastTimer);
  toastTimer = setTimeout(() => { node.hidden = true; }, 3200);
}

export function setFever(frac: number, active: boolean): void {
  const wrap = document.getElementById('fever')!;
  wrap.dataset.active = active ? '1' : '';
  const percent = Math.round(frac * 100);
  wrap.setAttribute('aria-valuenow', String(percent));
  document.getElementById('fever-fill')!.style.width = `${percent}%`;
}

let bossHitTimer: ReturnType<typeof setTimeout> | null = null;

export function setBoss(frac: number | null, hit = false): void {
  const bar = document.getElementById('boss-bar')!;
  bar.hidden = frac === null;
  if (frac !== null) document.getElementById('boss-fill')!.style.width = `${Math.round(frac * 100)}%`;
  if (frac === null) {
    bar.dataset.hit = '';
    if (bossHitTimer) clearTimeout(bossHitTimer);
    bossHitTimer = null;
  } else if (hit) {
    bar.dataset.hit = '1';
    if (bossHitTimer) clearTimeout(bossHitTimer);
    bossHitTimer = setTimeout(() => { bar.dataset.hit = ''; }, 130);
  }
}

let carryAngleBucket = NaN;
let carryDistanceKey = '';

export function setCarry(text: string | null): void {
  const node = el.carry();
  node.hidden = !text;
  node.dataset.search = text === 'NEXT LEADER' ? '1' : '';
  if (text) {
    const label = node.querySelector('.c-label')!;
    if (label.textContent !== text) label.textContent = text;
  } else {
    carryAngleBucket = NaN;
    carryDistanceKey = '';
    el.carryDistance().textContent = '';
    node.dataset.near = '';
    node.dataset.delivering = '';
  }
}

/** Keep the next objective explicit without turning the centre HUD into a
 * speedometer. Direction is degree-bucketed and long ranges use 5m steps. */
export function setCarryNavigation(
  deg: number, distance: number, delivering: boolean, target?: string,
): void {
  const node = el.carry();
  const angle = Math.round(deg);
  if (angle !== carryAngleBucket) {
    carryAngleBucket = angle;
    el.carryArrow().style.transform = `rotate(${angle}deg)`;
  }
  const metres = distance < 20 ? Math.ceil(distance) : Math.ceil(distance / 5) * 5;
  // The compass serves three errands now. A caller that owns a destination
  // names it; the Council legs keep the original search/stone pair.
  const destination = target ?? (node.dataset.search ? 'LEADER' : 'STONE');
  const key = delivering ? 'delivering' : `${destination}:${metres}`;
  if (key !== carryDistanceKey) {
    carryDistanceKey = key;
    el.carryDistance().textContent = delivering ? 'DESCENDING' : `${destination} ${metres}m`;
  }
  node.dataset.near = distance <= 14 ? '1' : '';
  node.dataset.delivering = delivering ? '1' : '';
}

/** The mobile cockpit grows in both directions — a leader card lengthens the
 * top lane, a new system deepens the lower bay — so the warning band cannot be
 * a fixed fraction of the screen. Measure the two edges per layout instead,
 * keyed on cheap reads: measuring every frame would force a layout, and the
 * band only moves when a row, an upgrade strip or a call appears. */
let threatBand = { top: 0, bottom: 0 };
let threatBandKey = '';

function measureThreatBand(w: number, h: number): { top: number; bottom: number } {
  let top = Math.min(150, h * 0.3);
  let bottom = h - Math.min(75, h * 0.18);
  if (!matchMedia('(pointer: coarse)').matches) return { top, bottom };
  const powers = document.getElementById('powers')!;
  const toast = el.toast();
  const key = `${w} ${h} ${powers.hidden ? 0 : powers.childElementCount}` +
    ` ${document.getElementById('build-hud')!.hidden} ${toast.hidden ? 0 : toast.childElementCount}`;
  if (key !== threatBandKey) {
    threatBandKey = key;
    // The centre lane is part of the top block in both orientations, so one
    // box gives the ceiling; the lowest thing the pill must clear gives the
    // floor. The joystick is named explicitly rather than trusted to sit under
    // an instrument: once the lower bay moved out of the flight view, the dock
    // stopped standing in for where the controls begin.
    const lane = document.querySelector('.hud-top')!.getBoundingClientRect();
    top = Math.max(top, lane.bottom);
    for (const id of ['systems-dock', 'mobile-flight-rail', 'pad']) {
      const rect = document.getElementById(id)!.getBoundingClientRect();
      if (rect.height > 0 && rect.top > 0) bottom = Math.min(bottom, rect.top);
    }
    // Half the pill plus a hairline, so the sweep's extremes clear both. When
    // a crowded phone leaves less room than that, hold the middle line.
    const inset = el.threat().offsetHeight / 2 + 3;
    const middle = (top + bottom) / 2;
    threatBand = top + inset > bottom - inset
      ? { top: middle, bottom: middle }
      : { top: top + inset, bottom: bottom - inset };
  }
  return threatBand;
}

/** Point toward the most urgent off-screen lock, shot, or threatened field. */
export function setThreat(
  deg: number | null,
  urgency = 0,
  count = 0,
  kind: 'lock' | 'shot' | 'field' = 'lock',
): void {
  const node = el.threat();
  if (deg === null || count <= 0) {
    node.hidden = true;
    return;
  }
  // Reveal before measuring: a hidden pill has no height, and the band it is
  // allowed to travel is defined against that height.
  const label = kind === 'field' ? 'FIELD' : kind === 'shot' ? 'SHOT' : 'LOCK';
  el.threatLabel().textContent = count > 1 ? `${label} ×${count}` : label;
  node.dataset.kind = kind;
  node.hidden = false;
  const rad = deg * Math.PI / 180;
  const dx = Math.cos(rad);
  const dy = Math.sin(rad);
  const w = window.innerWidth;
  const h = window.innerHeight;
  const edgeX = Math.min(52, w * 0.09);
  const band = measureThreatBand(w, h);
  const topSafe = band.top;
  // A crowded phone can leave less room than the pill is tall. Collapse the
  // sweep to the band's centre line rather than letting it ride an instrument.
  const bottomSafe = Math.max(band.bottom, topSafe);
  const centreY = (topSafe + bottomSafe) / 2;
  const x = w / 2 + dx * Math.min(380, w / 2 - edgeX);
  const y = centreY + dy * (bottomSafe - topSafe) / 2;
  let left = `${Math.round(x)}px`;
  // Landscape touch controls own the bottom corners. Pull a diagonal warning
  // just inside them without changing the direction its arrow communicates.
  // Keep the safe-area variables in the CSS expression: a notched landscape
  // phone moves its controls inward, so a fixed pixel clamp can still collide.
  if (matchMedia('(pointer: coarse)').matches && dy > 0.25) {
    if (dx < -0.25) left = `max(${left}, calc(210px + var(--sal)))`;
    if (dx > 0.25) left = `min(${left}, calc(100vw - 265px - var(--sar)))`;
  }
  node.style.left = left;
  node.style.top = `${Math.round(y)}px`;
  node.style.setProperty('--threat-urgency', String(urgency));
  node.dataset.urgent = urgency >= 0.58 ? '1' : '';
  el.threatArrow().style.transform = `rotate(${deg}deg)`;
}

const awardQueue: AwardDef[] = [];
let awardTimer: ReturnType<typeof setTimeout> | null = null;
let biomeAwardDelayMs = 4200;

function showNextAward(): void {
  const def = awardQueue.shift();
  if (!def) return;
  const node = document.getElementById('award-toast')!;
  document.getElementById('award-emoji')!.textContent = def.emoji;
  document.getElementById('award-name')!.textContent = `BADGE UNLOCKED · ${def.name}`;
  document.getElementById('award-desc')!.textContent = def.desc;
  node.hidden = false;
  awardTimer = setTimeout(() => {
    node.hidden = true;
    awardTimer = null;
    showNextAward();
  }, 3000);
}

export function awardToast(def: AwardDef): void {
  awardQueue.push(def);
  if (awardTimer) return;
  const biomeCard = document.getElementById('biome-card')!;
  if (!biomeCard.hidden) {
    awardTimer = setTimeout(() => {
      awardTimer = null;
      showNextAward();
    }, biomeAwardDelayMs);
    return;
  }
  showNextAward();
}

let biomeTimers: ReturnType<typeof setTimeout>[] = [];

/** Give a land reveal the world to itself. The title remains outside #hud, so
 * flight instruments can withdraw as one accessible unit and return together. */
function setArrivalFocus(active: boolean): void {
  const hud = el.hud();
  hud.inert = active;
  if (active) hud.setAttribute('aria-hidden', 'true');
  else hud.removeAttribute('aria-hidden');
}

/** Arcade title card: "TONIGHT'S HARVEST — <biome>", fades itself out. */
export function showBiomeCard(label: string, kicker = "TONIGHT'S HARVEST", durationMs = 4100): void {
  const node = document.getElementById('biome-card')!;
  // Arrival is a full-screen beat. Clear any event/departure toast and queue a
  // simultaneous badge until this title has had the centre to itself.
  const toast = el.toast();
  toast.hidden = true;
  if (toastTimer) { clearTimeout(toastTimer); toastTimer = null; }
  clearPendingBanner();
  node.querySelector('.bc-kicker')!.textContent = kicker;
  node.querySelector('.bc-name')!.textContent = label;
  for (const t of biomeTimers) clearTimeout(t);
  setArrivalFocus(true);
  node.hidden = false;
  requestAnimationFrame(() => { node.dataset.show = '1'; });
  const total = Math.max(1400, durationMs);
  const fadeAt = Math.max(700, total - 700);
  biomeAwardDelayMs = total + 100;
  biomeTimers = [
    setTimeout(() => { node.dataset.show = ''; }, fadeAt),
    setTimeout(() => {
      node.hidden = true;
      setArrivalFocus(false);
    }, total),
  ];
}

/** Remove an in-flight arrival card before a higher-priority full-screen UI. */
export function hideBiomeCard(): void {
  const node = document.getElementById('biome-card')!;
  for (const timer of biomeTimers) clearTimeout(timer);
  biomeTimers = [];
  node.dataset.show = '';
  node.hidden = true;
  setArrivalFocus(false);
}

let flashTimer: ReturnType<typeof setTimeout> | null = null;

/** Lightning screen flash: instant on, CSS-fade off. */
export function flash(strength: number): void {
  const node = document.getElementById('flash')!;
  node.style.transition = 'none';
  node.style.opacity = String(strength);
  if (flashTimer) clearTimeout(flashTimer);
  flashTimer = setTimeout(() => {
    node.style.transition = 'opacity 420ms ease-out';
    node.style.opacity = '0';
  }, 60);
}

export function banner(text: string, durationMs = 2200): void {
  const wait = bannerHoldUntil - performance.now();
  if (wait > 0) {
    pendingBanner = [text, durationMs];
    pendingTimer ??= setTimeout(() => {
      const next = pendingBanner;
      clearPendingBanner();
      if (next) banner(next[0], next[1]);
    }, wait);
    return;
  }
  const node = el.toast();
  node.innerHTML = '';
  const line = document.createElement('div');
  line.className = 't-name';
  line.textContent = text;
  node.append(line);
  node.hidden = false;
  if (toastTimer) clearTimeout(toastTimer);
  toastTimer = setTimeout(() => { node.hidden = true; }, durationMs);
}

/** The carry instruction is the next action, so it replaces any stale hint. */
export function hideBanner(): void {
  if (toastTimer) clearTimeout(toastTimer);
  toastTimer = null;
  clearPendingBanner();
  el.toast().hidden = true;
}
